/* eslint-disable */
import { useState, useEffect, useCallback } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
import { IconButton, Badge, Menu, MenuItem, Box, Typography, Chip, Divider, Button, Stack, CircularProgress } from "@mui/material";
import { IconBellRinging } from "@tabler/icons-react";
import { GET_SMART_ALERTS, GET_UNREAD_ALERT_COUNT } from "../../gql/queries/ai";
import { MARK_ALERT_READ, MARK_ALL_ALERTS_READ } from "../../gql/mutations/ai";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SEVERITY_COLORS = {
  error: "error",
  warning: "warning",
  info: "info",
};

/**
 * Return a human-readable relative time string.
 * Handles seconds, minutes, hours, and days.
 */
function timeAgo(dateValue) {
  if (!dateValue) return "";
  const date = new Date(Number(dateValue) || dateValue);
  if (isNaN(date.getTime())) return "";

  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 0) return "just now";

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function SmartAlertsDropdown() {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // ---- GQL: unread count (for badge) ----
  const [fetchUnreadCount, { data: countData }] = useLazyQuery(GET_UNREAD_ALERT_COUNT, {
    fetchPolicy: "network-only",
  });

  const unreadCount = countData?.getUnreadAlertCount ?? 0;

  // ---- GQL: full alerts list (for dropdown) ----
  const [fetchAlerts, { data: alertsData, loading: alertsLoading }] = useLazyQuery(GET_SMART_ALERTS, {
    fetchPolicy: "network-only",
  });

  const alerts = alertsData?.getSmartAlerts ?? [];

  // ---- GQL: mutations ----
  const [markRead] = useMutation(MARK_ALERT_READ);
  const [markAllRead] = useMutation(MARK_ALL_ALERTS_READ);

  // ---- Poll for unread count every 60 seconds ----
  useEffect(() => {
    // Initial fetch
    fetchUnreadCount();

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 60_000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Open / Close handlers ----
  const handleOpen = event => {
    setAnchorEl(event.currentTarget);
    fetchAlerts();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // ---- Mark single alert as read ----
  const handleMarkRead = useCallback(
    async alertId => {
      try {
        await markRead({ variables: { id: alertId } });
        // Optimistically update the local list
        fetchAlerts();
        fetchUnreadCount();
      } catch {
        // silent
      }
    },
    [markRead, fetchAlerts, fetchUnreadCount],
  );

  // ---- Mark all as read ----
  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllRead();
      fetchAlerts();
      fetchUnreadCount();
    } catch {
      // silent
    }
  }, [markAllRead, fetchAlerts, fetchUnreadCount]);

  // -------------------------------------------
  // Render
  // -------------------------------------------

  return (
    <Box>
      <IconButton
        size="large"
        aria-label="Smart alerts"
        color="inherit"
        aria-controls="smart-alerts-menu"
        aria-haspopup="true"
        onClick={handleOpen}
        sx={{
          color: menuOpen ? "primary.main" : "text.secondary",
        }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <IconBellRinging size="21" stroke="1.5" />
        </Badge>
      </IconButton>

      <Menu
        id="smart-alerts-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        keepMounted
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        sx={{
          "& .MuiMenu-paper": {
            width: 360,
            maxHeight: 400,
            overflowY: "auto",
          },
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography variant="h6" fontWeight={600}>
            Smart Alerts
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead}>
              Mark All Read
            </Button>
          )}
        </Stack>

        <Divider />

        {/* Loading state */}
        {alertsLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Empty state */}
        {!alertsLoading && alerts.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No alerts
            </Typography>
          </Box>
        )}

        {/* Alert items */}
        {!alertsLoading &&
          alerts.map(alert => (
            <MenuItem
              key={alert.id}
              onClick={() => {
                if (!alert.isRead) {
                  handleMarkRead(alert.id);
                }
              }}
              sx={{
                whiteSpace: "normal",
                alignItems: "flex-start",
                py: 1.5,
                px: 2,
                backgroundColor: alert.isRead ? "transparent" : "action.hover",
                "&:hover": {
                  backgroundColor: alert.isRead ? "action.hover" : "action.selected",
                },
              }}
            >
              <Box sx={{ width: "100%" }}>
                {/* Top row: severity chip + time */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Chip
                    label={alert.severity || "info"}
                    size="small"
                    color={SEVERITY_COLORS[alert.severity] || "default"}
                    sx={{ height: 20, fontSize: "0.7rem", textTransform: "capitalize" }}
                  />
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.7rem" }}>
                    {timeAgo(alert.createdAt)}
                  </Typography>
                </Stack>

                {/* Title */}
                <Typography
                  variant="subtitle2"
                  fontWeight={alert.isRead ? 400 : 600}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    mb: 0.3,
                  }}
                >
                  {alert.title}
                </Typography>

                {/* Message (2-line truncation) */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    fontSize: "0.8rem",
                    lineHeight: 1.4,
                  }}
                >
                  {alert.message}
                </Typography>
              </Box>
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
}

export default SmartAlertsDropdown;
